'use client'
import { usePathname } from '@/src/i18n/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGetCurrencyDataQuery } from '@/src/api/rtkQuery/currencyApi'
import { currencyOptions, languageOptions } from '@/src/constants/header'
import { clearAuthUser, refreshAuthUser, selectAuth } from '@/src/redux/slices/authSlice'
import { setCurrency } from '@/src/redux/slices/currencySlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import { deleteCookie } from '@/src/utils/browser'
import { mapApiCurrencyOptions } from '@/src/utils/currency'
import { getLanguageByCode, setLanguageCookie } from '@/src/utils/language'
import { getActiveVertical } from '@/src/utils/navigation/getActiveVertical'
import FlightIcon from '@mui/icons-material/Flight'
import HotelIcon from '@mui/icons-material/Hotel'

import { CTHeader } from './CTHeader'

// Dynamically load SignInModal to reduce initial bundle size
const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../flyinHeader/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

interface CTHeaderContainerProps {
    hideSignIn?: boolean
}

const CTHeaderContainer: React.FC<CTHeaderContainerProps> = ({ hideSignIn = false }) => {
    const [isMounted, setIsMounted] = useState(false)
    const [modals, setModals] = useState<{ signIn: boolean }>({ signIn: false })
    const userMenuAnchorRef = useRef<HTMLElement | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [selected, setSelected] = useState({
        language: languageOptions[0],
        currency: currencyOptions[0]
    })
    const locale = useLocale()
    const localePrefix = useMemo(() => `/${locale}`, [locale])

    // Fetch currency list from API
    const { data: currencyData, isLoading: isCurrencyLoading, isError: isCurrencyError } = useGetCurrencyDataQuery()

    // Map API data to expected shape for dropdown (using utility)
    const apiCurrencyOptions = React.useMemo(() => mapApiCurrencyOptions(currencyData), [currencyData])

    const currency = useAppSelector(state => state.currency.selected)
    const dispatch = useAppDispatch()

    const t = useTranslations('header')
    const tCurrency = useTranslations('Currency')
    const user = useAppSelector(selectAuth)
    const pathname = usePathname()

    // Determine active vertical based on current pathname
    const activeVertical = useMemo(() => {
        if (!isMounted) return null
        return getActiveVertical()
    }, [isMounted, pathname])

    // Define which routes should show the language/currency dropdown
    const showLangCurrDropdown = React.useMemo(() => {
        if (pathname === '/' || /^\/[a-z]{2}$/.test(pathname)) return true
        if (pathname === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(pathname)) return true
        return false
    }, [pathname])

    // Check if current page is offers page - disable sign in on offers page
    const isOffersPage = pathname.includes('/offers')

    // Check if we're on itinerary page - always hide sign-in on itinerary page
    const isItineraryPage = pathname.includes('/flights/itinerary')
    const shouldHideSignInOnItinerary = isItineraryPage

    // Show Deals link on specific pages
    const showDeals = React.useMemo(() => {
        if (pathname === '/' || /^\/[a-z]{2}$/.test(pathname)) return true
        if (pathname === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(pathname)) return true
        if (pathname.includes('/offers')) return true
        return false
    }, [pathname])

    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined') return
        dispatch(refreshAuthUser())
        setIsMounted(true)
    }, [dispatch])

    useEffect(() => {
        if (!isMounted) return

        // Update currency if needed
        if (!isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0) {
            const found = apiCurrencyOptions.find((opt: any) => opt.code === currency)
            if (found) {
                setSelected(prev => ({
                    ...prev,
                    currency: found
                }))
            }
        }

        // Read persisted language (using utility)
        const persistedLang = localStorage.getItem('selectedLanguage')
        if (persistedLang) {
            const found = getLanguageByCode(persistedLang)
            setSelected(prev => ({
                ...prev,
                language: found
            }))
        }
    }, [isMounted, currency, apiCurrencyOptions, isCurrencyLoading, isCurrencyError])

    const options = {
        language: languageOptions,
        currency:
            !isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0
                ? apiCurrencyOptions
                : currencyOptions // fallback to static
    }

    const onMenuOpen = useCallback((menu: 'user', event: React.MouseEvent<HTMLElement>) => {
        userMenuAnchorRef.current = event.currentTarget
        setMenuOpen(true)
    }, [])

    const onMenuClose = useCallback((menu: 'user') => {
        userMenuAnchorRef.current = null
        setMenuOpen(false)
    }, [])

    const onSelect = useCallback(
        (type: 'language' | 'currency', value: any) => {
            setSelected(prev => ({ ...prev, [type]: value }))
            if (type === 'currency') {
                dispatch(setCurrency({ code: value.code, symbol: value.symbol }))
            } else if (type === 'language') {
                setLanguageCookie(value.code)
                localStorage.setItem('selectedLanguage', value.code)
                // Always set path to /ar/ or /en/ at the root
                const base = window.location.href
                const newPath = value.code === 'ar' ? base.replace('/en', '/ar') : base.replace('/ar', '/en')
                window.location.href = newPath
            }
            onMenuClose('user')
        },
        [dispatch, onMenuClose]
    )

    const onModalOpen = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: true }))
    }, [])

    const onModalClose = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: false }))
    }, [])

    // Sign out handler
    const handleSignOut = useCallback(() => {
        dispatch(clearAuthUser())
        const domain = '.' + window.location.hostname
        deleteCookie('usermisc', domain)
        deleteCookie('userid', domain)
        deleteCookie('userData', domain)
        dispatch(refreshAuthUser())
        onMenuClose('user')
    }, [dispatch, onMenuClose])

    // User menu items
    const userMenuItems = useMemo(
        () => [
            { label: t('userMenu.myBookings'), href: '/accounts/trips' },
            { label: t('userMenu.myWallet'), href: `/${locale}/wallet` },
            { label: t('userMenu.myReviews'), href: '/manageReviews' },
            { label: t('userMenu.giveFeedback'), href: '/feedback' }
        ],
        [t]
    )

    // Icon mapping for nav items
    const getNavIcon = useCallback((label: string) => {
        const iconMap: { [key: string]: React.ReactNode } = {
            flights: <FlightIcon sx={{ fontSize: 20, color: 'inherit' }} />,
            hotels: <HotelIcon sx={{ fontSize: 20, color: 'inherit' }} />
        }
        return iconMap[label.toLowerCase()] || null
    }, [])

    // Create nav links for Cleartrip with active state
    const ctNavLinks = useMemo(() => {
        return [
            { label: 'Flights', href: `${localePrefix}/flights`, isActive: activeVertical === 'flights' },
            { label: 'Hotels', href: `${localePrefix}/hotels`, isActive: activeVertical === 'hotels' }
        ]
    }, [activeVertical, localePrefix])

    const displayUser = isMounted ? user : { signedIn: false }

    return (
        <>
            <CTHeader
                navLinks={ctNavLinks}
                options={options}
                selected={selected}
                menuAnchors={{ user: menuOpen ? userMenuAnchorRef.current : null }}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                onSelect={onSelect}
                onModalOpen={onModalOpen}
                t={t}
                tCurrency={tCurrency}
                user={displayUser}
                userMenuItems={userMenuItems}
                onSignOut={handleSignOut}
                showLangCurrDropdown={showLangCurrDropdown}
                getNavIcon={getNavIcon}
                hideSignIn={isOffersPage || shouldHideSignInOnItinerary || hideSignIn}
                showDeals={showDeals}
            />
            <SignInModalWrapper
                open={modals.signIn}
                onClose={() => onModalClose('signIn')}
                onMenuClose={() => onMenuClose('user')}
            />
        </>
    )
}

export { CTHeaderContainer }
export default CTHeaderContainer
